import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { hideDialog } from '../../../dialog/actions';
import { IconCloseLarge } from '../../../icons/svg';
import { withPixelLineHeight } from '../../../styles/functions.web';
import { operatesWithEnterKey } from '../../functions.web';
import BaseDialog from './BaseDialog';
import Button from './Button';
import ClickableIcon from './ClickableIcon';
import Tooltip from '../../../tooltip/components/Tooltip';
import { Box } from '@mui/material';
const useStyles = makeStyles()(theme => {
    return {
        header: {
            width: '100%',
            padding: '24px 40px 8px 40px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            '& svg': {
                fill: `${theme.palette.default00} !important`
            }
        },
        title: {
            color: theme.palette.text03,
            ...withPixelLineHeight(theme.typography.heading5),
            margin: 0,
            padding: 0,
            fontWeight: 700,
            fontSize: '24px'
        },
        content: {
            height: 'auto',
            overflowY: 'auto',
            width: 'calc(100% - 8px)',
            boxSizing: 'border-box',
            padding: '0 36px',
            margin: '4px',
            overflowX: 'hidden',
            minHeight: '40px',
            fontSize: '16px',
            fontWeight: 500,
            '@media (max-width: 448px)': {
                height: '100%'
            }
        },
        footer: {
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '24px 40px',
            position: 'relative',
            '& button': {
                height: '36px'
            },
            '& button:last-child': {
                marginLeft: '16px'
            }
        }
    };
});
const Dialog = ({ back = { hidden: true }, cancel = { translationKey: 'dialog.Cancel' }, ok = { translationKey: 'dialog.Ok' }, children, className, description, disableAutoHideOnSubmit = false, disableBackdropClose, hideCloseButton, hideButtons, disableEnter, disableEscape, onCancel, onSubmit, size, testId, title, titleKey, hideTooltip, customElmFooter, contentRef }) => {
    const { cx, classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const onClose = useCallback(() => {
        dispatch(hideDialog());
        onCancel?.();
    }, [onCancel]);
    const submit = useCallback(() => {
        if ((document.activeElement && !operatesWithEnterKey(document.activeElement)) || !document.activeElement) {
            !disableAutoHideOnSubmit && dispatch(hideDialog());
            onSubmit?.();
        }
    }, [onSubmit]);
    return (React.createElement(BaseDialog, { className: className, description: description, disableBackdropClose: disableBackdropClose, disableEnter: disableEnter, disableEscape: disableEscape, onClose: onClose, size: size, submit: submit, testId: testId, title: title, titleKey: titleKey },
        React.createElement("div", { className: classes.header },
            React.createElement("h1", { className: classes.title, id: 'dialog-title' }, title ?? t(titleKey ?? '')),
            !hideCloseButton && (React.createElement(Tooltip, { content: t('dialog.accessibilityLabel.close') },
                React.createElement(Box, { sx: { position: 'absolute', right: '18px', top: '18px' } },
                    React.createElement(ClickableIcon, { accessibilityLabel: t('dialog.accessibilityLabel.close'), icon: IconCloseLarge, id: 'modal-header-close-button', onClick: onClose }))))),
        React.createElement("div", { ref: contentRef, className: classes.content, "data-autofocus-inside": 'true' }, children),
        !hideButtons && React.createElement("div", { className: classes.footer, "data-autofocus-inside": 'true' },
            customElmFooter,
            !back.hidden && React.createElement(Button, { accessibilityLabel: !hideTooltip ? t(back.translationKey ?? '') : '', labelKey: back.translationKey, 
                // eslint-disable-next-line react/jsx-handler-names
                onClick: back.onClick, type: 'secondary' }),
            !cancel.hidden && React.createElement(Button, { accessibilityLabel: !hideTooltip ? t(cancel.translationKey ?? '') : '', labelKey: cancel.translationKey, onClick: onClose, type: 'secondary' }),
            !ok.hidden && React.createElement(Button, { accessibilityLabel: !hideTooltip ? t(ok.translationKey ?? '') : '', disabled: ok.disabled, id: 'modal-dialog-ok-button', isSubmit: true, labelKey: ok.translationKey, ...(!ok.disabled && { onClick: submit }) }))));
};
export default Dialog;
